// PDF do demonstrativo de remuneração (E8): para cada pessoa, o que ela
// ganhou no período, mês a mês, o que ainda falta acertar e o que recebeu, com
// lugar para a assinatura da empresa (serve de comprovante de renda). As
// fontes padrão do PDF não têm o sinal de menos tipográfico: vai o hífen.
import { cpf as formatarCpf, data as formatarData, documento as formatarDocumento, mesCurto, numeroBR } from './formato.js';
import { rodape } from './demonstrativo.js';
import { periodoDoRelatorio } from './pdfcaminhoes.js';

export const NOMES_NO_DEMONSTRATIVO = {
  comissao: 'Comissões',
  valor_por_carga: 'Valor por carga',
  valor_por_viagem: 'Valor por viagem',
  diaria: 'Diárias',
  repasse_estadia: 'Repasse da estadia',
  fixo_mensal: 'Fixo mensal',
  folha: 'Fixo pago pela folha (pelo cadastro)',
  agenciamento: 'Agenciamentos',
  bonus: 'Bônus',
  outro: 'Outros créditos',
};
const COLUNA = {
  comissao: 'Comissão', valor_por_carga: 'Por carga', valor_por_viagem: 'Por viagem', diaria: 'Diárias', repasse_estadia: 'Estadia',
  fixo_mensal: 'Fixo', folha: 'Folha', agenciamento: 'Agenciam.', bonus: 'Bônus', outro: 'Outros',
};
export const NOMES_DOS_DESCONTOS = { avaria: 'Avaria', multa: 'Multa', outro: 'Outros descontos' };
const FUNCAO = { motorista: 'motorista', agenciador: 'agenciador', outro: 'colaborador' };
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

const valor = (v) => numeroBR(v, { casas: 2 });
const reais = (v) => (v < 0 ? `-R$ ${valor(-v)}` : `R$ ${valor(v)}`);

export function nomeDaPessoa(c) {
  return c ? `${c.nome}${c.apelido ? ` (${c.apelido})` : ''}` : 'Colaborador';
}

export function nomeDoArquivoDosGanhos({ de, ate, colaborador = null }) {
  const quem = colaborador
    ? (colaborador.apelido || colaborador.nome).normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase()
    : 'todos';
  const mes = (d) => d.slice(0, 7);
  return mes(de) === mes(ate) ? `remuneracao-${quem}-${mes(de)}.pdf` : `remuneracao-${quem}-${mes(de)}-a-${mes(ate)}.pdf`;
}

// As colunas da tabela mês a mês: os tipos de crédito que aparecem (no
// máximo cinco; os menores vão para "Outros").
export function colunasDosGanhos(g) {
  const totais = { ...g.totais.creditos };
  if (g.totais.folha) totais.folha = g.totais.folha;
  const ordem = ['comissao', 'valor_por_carga', 'valor_por_viagem', 'diaria', 'repasse_estadia', 'fixo_mensal', 'folha', 'agenciamento', 'bonus', 'outro'];
  let tipos = ordem.filter((k) => totais[k]);
  if (tipos.length > 5) {
    const maiores = [...tipos].sort((a, b) => totais[b] - totais[a]).slice(0, 4);
    tipos = [...ordem.filter((k) => maiores.includes(k)), 'resto'];
  }
  return tipos;
}

export function valorDaColuna(m, tipo, tipos) {
  if (tipo === 'folha') return m.folha ?? 0;
  if (tipo !== 'resto') return m.creditos[tipo] ?? 0;
  const usados = new Set(tipos);
  let c = Object.entries(m.creditos).filter(([k]) => !usados.has(k)).reduce((s, [, v]) => s + Math.round(v * 100), 0);
  if (!usados.has('folha')) c += Math.round((m.folha ?? 0) * 100);
  return c / 100;
}

// pessoas: [{ colaborador, ganhos }]
export async function ganhosEmPdf({ pessoas, empresa, emitidoEm = new Date() }) {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const margem = 18;
  const direita = 210 - margem;
  const fim = 280;
  const fuso = empresa.fuso_horario || 'America/Sao_Paulo';
  const dia = new Intl.DateTimeFormat('pt-BR', { timeZone: fuso, day: '2-digit', month: '2-digit', year: 'numeric' }).format(emitidoEm);
  const emitido = `${dia} às ${new Intl.DateTimeFormat('pt-BR', { timeZone: fuso, hour: '2-digit', minute: '2-digit' }).format(emitidoEm)}`;
  const [d, m, a] = dia.split('/');
  const porExtenso = `${Number(d)} de ${MESES[Number(m) - 1]} de ${a}`;
  const docEmpresa = empresa.documento ? `${empresa.tipo_pessoa === 'PF' ? 'CPF' : 'CNPJ'} ${formatarDocumento(empresa.documento)}` : '';
  let y = 24;

  const texto = (t, x, opcoes = {}) => pdf.text(String(t).replace(/−/g, '-'), x, y, opcoes);
  const cabe = (altura) => {
    if (y + altura > fim) {
      pdf.addPage();
      y = 20;
    }
  };
  const pequeno = (t) => {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(90);
    const linhas = pdf.splitTextToSize(String(t).replace(/−/g, '-'), direita - margem);
    cabe(linhas.length * 3.6 + 1);
    pdf.text(linhas, margem, y);
    pdf.setTextColor(0);
    y += linhas.length * 3.6 + 1.4;
  };
  const linhaValor = (rotulo, v, { negrito = false, recuo = 2 } = {}) => {
    cabe(6);
    pdf.setFont('helvetica', negrito ? 'bold' : 'normal');
    pdf.setFontSize(9.5);
    const partes = pdf.splitTextToSize(String(rotulo).replace(/−/g, '-'), 125);
    pdf.text(partes, margem + recuo, y);
    texto(reais(v), direita, { align: 'right' });
    y += partes.length * 4.4 + 0.8;
  };

  pessoas.forEach(({ colaborador: c, ganhos: g }, i) => {
    if (i > 0) pdf.addPage();
    y = 24;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(15);
    texto('DEMONSTRATIVO DE REMUNERAÇÃO', margem);
    y += 7;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    texto(`${empresa.nome}${docEmpresa ? ` · ${docEmpresa}` : ''}`, margem);
    y += 5;
    pdf.setFont('helvetica', 'bold');
    texto(`${nomeDaPessoa(c)}${c?.cpf ? ` · CPF ${formatarCpf(c.cpf)}` : ''}${c?.funcao ? ` · ${FUNCAO[c.funcao] ?? c.funcao}` : ''}`, margem);
    y += 5;
    pdf.setFont('helvetica', 'normal');
    texto(`Período: ${periodoDoRelatorio(g)} (${formatarData(g.de)} a ${formatarData(g.ate)}) · emitido em ${emitido}`, margem);
    y += 5.5;
    pequeno('Valores dos acertos fechados no HSA Frota e do histórico dos programas antigos, pela data de cada lançamento: a da carga na comissão e no agenciamento, '
      + 'o último dia pago nas diárias, o mês no fixo. O fixo pago pela folha vem do cadastro. Vales e pagamentos aparecem em "Recebido no período".');
    pdf.setLineWidth(0.3);
    pdf.line(margem, y, direita, y);
    y += 6;

    // Mês a mês
    const tipos = colunasDosGanhos(g);
    const colunas = [...tipos.map((t) => (t === 'resto' ? 'Outros' : COLUNA[t])), 'Descontos', 'Total'];
    const largura = (direita - (margem + 22)) / colunas.length;
    const xDa = (i2) => margem + 22 + largura * (i2 + 1);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(90);
    texto('Mês', margem);
    colunas.forEach((rotulo, i2) => texto(rotulo, xDa(i2), { align: 'right' }));
    pdf.setTextColor(0);
    y += 1.5;
    pdf.setLineWidth(0.2);
    pdf.line(margem, y, direita, y);
    y += 4;
    const linha = (rotulo, valores, negrito = false) => {
      cabe(5);
      pdf.setFont('helvetica', negrito ? 'bold' : 'normal');
      pdf.setFontSize(8.5);
      texto(rotulo, margem);
      valores.forEach((v, i2) => texto(valor(v), xDa(i2), { align: 'right' }));
      y += 4.4;
    };
    for (const mes of g.meses) {
      linha(mesCurto(mes.mes), [...tipos.map((t) => valorDaColuna(mes, t, tipos)), mes.totalDescontos ? -mes.totalDescontos : 0, mes.ganho]);
    }
    pdf.line(margem, y - 3.2, direita, y - 3.2);
    const total = { creditos: g.totais.creditos, folha: g.totais.folha };
    linha('Total', [...tipos.map((t) => valorDaColuna(total, t, tipos)), g.totais.totalDescontos ? -g.totais.totalDescontos : 0, g.totais.ganho], true);
    y += 3;

    // Resumo do período
    cabe(20);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    texto('Resumo do período', margem);
    y += 5;
    for (const k of ['comissao', 'valor_por_carga', 'valor_por_viagem', 'diaria', 'repasse_estadia', 'fixo_mensal', 'agenciamento', 'bonus', 'outro']) {
      if (g.totais.creditos[k]) linhaValor(NOMES_NO_DEMONSTRATIVO[k], g.totais.creditos[k]);
    }
    if (g.totais.folha) linhaValor(NOMES_NO_DEMONSTRATIVO.folha, g.totais.folha);
    for (const k of ['avaria', 'multa', 'outro']) {
      if (g.totais.descontos[k]) linhaValor(`- ${NOMES_DOS_DESCONTOS[k]}`, -g.totais.descontos[k]);
    }
    linhaValor('= Total ganho no período', g.totais.ganho, { negrito: true });
    y += 2;

    if (g.aAcertar.itens.length > 0) {
      cabe(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      texto('Ainda não acertado (fora do total)', margem);
      y += 5;
      for (const x of g.aAcertar.itens) {
        linhaValor(`${formatarData(x.data)} · ${x.descricao || NOMES_NO_DEMONSTRATIVO[x.subtipo] || 'Crédito'}${x.lancado ? '' : ' (pelas regras)'}`, x.valor);
      }
      linhaValor('= Total a acertar', g.aAcertar.total, { negrito: true });
      y += 2;
    }

    cabe(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    texto('Recebido no período', margem);
    y += 5;
    linhaValor('Vales (adiantamentos)', g.recebido.vales);
    linhaValor('Pagamentos dos acertos', g.recebido.pagamentos);
    if (g.recebido.devolucoes) linhaValor('- Devolvido à empresa', -g.recebido.devolucoes);
    linhaValor('= Total recebido', g.recebido.total, { negrito: true });
    if (g.recebido.despesas) {
      linhaValor('- Reembolso das despesas que pagou (diesel, pedágio...)', -g.recebido.despesas);
      linhaValor('= Recebido pela remuneração', g.recebido.liquido, { negrito: true });
    }
    if (g.historico) pequeno('Parte do período veio dos programas antigos, que não registravam os pagamentos das comissões: no recebido aparecem só os vales.');
    for (const s of g.implantacao) {
      pequeno(`Saldo trazido dos programas antigos em ${formatarData(s.data)}: ${reais(s.valor)}. Não entra no total ganho: é o que estava em aberto na troca de programa.`);
    }

    // Assinatura
    cabe(34);
    y += 12;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    texto(`${empresa.cidade ? `${empresa.cidade}${empresa.uf ? `/${empresa.uf}` : ''}, ` : ''}${porExtenso}.`, margem);
    y += 16;
    pdf.setLineWidth(0.3);
    pdf.line(margem, y, margem + 90, y);
    y += 4.5;
    texto(empresa.nome, margem);
    if (docEmpresa) {
      y += 4.5;
      texto(docEmpresa, margem);
    }
  });

  rodape(pdf, `demonstrativo de remuneração de ${periodoDoRelatorio(pessoas[0]?.ganhos ?? { de: '0000-00-00', ate: '0000-00-00' })}`);
  return pdf.output('blob');
}
